import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { http } from "../api";
import { Search, User, Phone, X, ArrowRight, Loader2 } from "lucide-react";

/**
 * Global ⌘K / Ctrl+K search palette that jumps to any voter's journey.
 * Mount once inside the authenticated Shell.
 */
export default function VoterSearchPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);
  const navigate = useNavigate();

  // Global keyboard shortcut ⌘K / Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      if (mod && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  // Focus the input when opened
  useEffect(() => {
    if (open) {
      setQ(""); setRows([]); setCursor(0);
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) { setRows([]); setLoading(false); return; }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await http.get(`/contacts?q=${encodeURIComponent(q)}`);
        setRows((r.data || []).slice(0, 12));
        setCursor(0);
      } finally { setLoading(false); }
    }, 220);
  }, [q, open]);

  const highlighted = useMemo(() => rows[cursor] || null, [rows, cursor]);

  const goTo = (c) => {
    setOpen(false);
    navigate(`/journey/${c.contact_id}`);
  };

  const onInputKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setCursor((c) => Math.min(rows.length - 1, c + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    else if (e.key === "Enter" && highlighted) { e.preventDefault(); goTo(highlighted); }
  };

  if (!open) return null;

  return (
    <div className="vsearch-backdrop" data-testid="voter-search-palette" onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
      <div className="vsearch-shell">
        <div className="vsearch-input-row">
          <Search size={16} />
          <input
            ref={inputRef}
            placeholder="Search by name, mobile or EPIC…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onInputKey}
            data-testid="voter-search-input"
          />
          {loading ? <Loader2 size={14} className="vsearch-spin" /> : (
            <button className="icon-btn" onClick={() => setOpen(false)} data-testid="voter-search-close"><X size={14} /></button>
          )}
        </div>
        <div className="vsearch-results" data-testid="voter-search-results">
          {q.trim() && !loading && rows.length === 0 && (
            <div className="vsearch-empty">No voters found for &ldquo;{q}&rdquo;.</div>
          )}
          {!q.trim() && (
            <div className="vsearch-hint">
              <b>Tip:</b> Start typing a name, mobile number, or EPIC card.
              <br /><small>Use ↑ ↓ to navigate · Enter to open · Esc to close</small>
            </div>
          )}
          {rows.map((c, i) => (
            <button
              key={c.contact_id}
              className={`vsearch-row ${i === cursor ? "active" : ""}`}
              data-testid={`voter-search-row-${i}`}
              onMouseEnter={() => setCursor(i)}
              onClick={() => goTo(c)}
            >
              <span className="vsearch-avatar">{(c.name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase()}</span>
              <span className="vsearch-info">
                <b>{c.name}</b>
                <small>
                  {c.gender ? `${c.gender} · ` : ""}{c.age ? `${c.age}yr · ` : ""}
                  Booth {c.booth || "?"} · {c.inclination || "Undecided"}
                  {c.mobile ? <> · <Phone size={9} style={{ verticalAlign: "middle" }} /> {c.mobile}</> : null}
                  {c.epic ? ` · EPIC ${c.epic}` : ""}
                </small>
              </span>
              {i === cursor && <ArrowRight size={14} className="vsearch-cta" />}
            </button>
          ))}
        </div>
        <div className="vsearch-foot">
          <span><kbd>↑</kbd><kbd>↓</kbd> Move</span>
          <span><kbd>↵</kbd> Open</span>
          <span><kbd>Esc</kbd> Close</span>
          <span className="vsearch-brand"><User size={11} /> Ward 7 CRM</span>
        </div>
      </div>
    </div>
  );
}
